import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  Loader2,
  MoreHorizontalIcon,
  PlusIcon,
} from 'lucide-react'
import { toast } from 'sonner'
import { z } from 'zod'

import { useSmartForm } from '@/hooks/use-smart-form.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx'
import { Input } from '@/components/ui/input.tsx'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from '@/components/ui/pagination'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { confirm } from '@/components/confirmation.tsx'
import { DateFormat } from '@/components/date-format.tsx'
import { Breadcrumbs } from '@/components/layout/breadcrumbs.tsx'
import { PageTitle } from '@/components/layout/page-title.tsx'
import { QuerySpinner } from '@/components/spinner/query-spinner.tsx'
import { useDeleteUser } from '@/features/users/hooks/use-delete-user.tsx'
import {
  findUsersQueryOptions,
  useFindUsers,
} from '@/features/users/hooks/use-find-users.ts'
import { useInviteUser } from '@/features/users/hooks/use-invite-user.tsx'

type UserRow = {
  id: string
  email: string
  createdAt?: string | Date | null
}

const searchParamsSchema = z.object({
  page: z.coerce.number().optional().default(1),
  perPage: z.coerce.number().optional().default(10),
  inviteModalOpen: z.coerce.boolean().optional(),
})

type SearchParamsSchema = z.infer<typeof searchParamsSchema>

export const Route = createFileRoute('/_authenticated/settings/users/')({
  component: UsersList,
  validateSearch: searchParamsSchema,
})

export default function UsersList() {
  const searchParams = Route.useSearch()
  const { page, perPage, inviteModalOpen } = searchParams
  const navigate = Route.useNavigate()

  const usersQuery = useFindUsers({
    page: page ? String(page) : undefined,
    perPage: perPage ? String(perPage) : undefined,
  })
  const usersRaw = usersQuery?.data?.data
  const users: UserRow[] = Array.isArray(usersRaw)
    ? usersRaw.filter((u: unknown): u is UserRow => {
        if (!u || typeof u !== 'object') return false
        const record = u as Record<string, unknown>
        return (
          typeof record.id === 'string' &&
          typeof record.email === 'string' &&
          (record.createdAt === undefined ||
            typeof record.createdAt === 'string' ||
            record.createdAt instanceof Date ||
            record.createdAt === null)
        )
      })
    : []

  const handlePageChange = (newPage: number) => {
    navigate({ search: { perPage, page: newPage } })
  }

  const isNextDisabled =
    users.length === 0 ||
    usersQuery.isFetching ||
    users.length < perPage

  const inviteNewUserForm = useSmartForm<{ email: string }>()
  const inviteUser = useInviteUser()
  const handleNewUserFormSubmit = async (data: { email: string }) => {
    try {
      await inviteUser.mutateAsync(data)
    } catch {
      // The API now reports a failed invite with a non-2xx status, which
      // `formatClientResponse` turns into a throw. Without this the success
      // toast fired regardless of the outcome.
      toast('Invito non riuscito, riprova', { icon: <CircleAlertIcon /> })
      return
    }

    toast('Utente invitato', { icon: <CircleCheckIcon /> })
    navigate({ search: { ...searchParams, inviteModalOpen: undefined } })
  }

  const deleteUser = useDeleteUser()
  const queryClient = useQueryClient()
  const handleDeleteBtnClick = (user: UserRow) => {
    confirm({
      title: `Sei sicuro di voler eliminare l'utente ${user.email}?`,
      okButtonVariant: 'destructive',
      okText: 'Conferma',
      async onOk() {
        await deleteUser.mutateAsync({ userId: user.id })
        await queryClient.invalidateQueries(
          findUsersQueryOptions({
            page: page ? String(page) : undefined,
            perPage: perPage ? String(perPage) : undefined,
          })
        )

        toast('Utente eliminato', { icon: <CircleCheckIcon /> })
      },
    })
  }

  return (
    <>
      <Breadcrumbs items={[{ name: 'Impostazioni' }, { name: 'Utenti' }]} />
      <ScrollArea className="p-4">
        <div className="mb-2 flex justify-between">
          <PageTitle>Utenti</PageTitle>

          <Link
            to="/settings/users"
            search={(search) =>
              ({ ...search, inviteModalOpen: true }) as SearchParamsSchema
            }
          >
            <Button variant="outline" size="sm">
              <PlusIcon /> Invita utente
            </Button>
          </Link>
        </div>

        <QuerySpinner query={usersQuery} wrapperClassName="py-10">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead className="max-w-36 truncate">ID</TableHead>
                <TableHead>Data aggiunta</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users?.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.email}</TableCell>
                  <TableCell className="max-w-36 truncate" title={user.id}>
                    {user.id}
                  </TableCell>
                  <TableCell>
                    <DateFormat
                      date={user.createdAt}
                      format="DD/MM/YYYY HH:mm"
                    />
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-green-400" variant="default">
                      Attivo
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="data-[state=open]:bg-muted flex size-8 p-0"
                        >
                          <MoreHorizontalIcon />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[160px]">
                        <DropdownMenuItem
                          onClick={() => handleDeleteBtnClick(user)}
                        >
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="mt-4 flex justify-end">
            <Pagination className="mx-0 inline w-auto">
              <PaginationContent>
                <PaginationItem>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <ChevronLeftIcon className="size-4" />
                  </Button>
                </PaginationItem>
                <PaginationItem>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={isNextDisabled}
                  >
                    <ChevronRightIcon className="size-4" />
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </QuerySpinner>
      </ScrollArea>

      <Dialog
        open={inviteModalOpen}
        onOpenChange={() =>
          navigate({ search: { ...searchParams, inviteModalOpen: undefined } })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invita un nuovo utente</DialogTitle>
            {/*<DialogDescription>si</DialogDescription>*/}
          </DialogHeader>
          <Form {...inviteNewUserForm}>
            <form
              onSubmit={inviteNewUserForm.handleSubmit(handleNewUserFormSubmit)}
            >
              <div className="grid gap-4 py-2">
                <FormField
                  control={inviteNewUserForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <FormDescription></FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="flex justify-end gap-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    navigate({
                      search: { ...searchParams, inviteModalOpen: undefined },
                    })
                  }
                >
                  Annulla
                </Button>
                <Button disabled={inviteUser.isPending}>
                  {inviteUser.isPending && (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  )}
                  Invita
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  )
}
